import { useCallback, useRef } from 'react';
import { useMatrixContext } from '../MatrixContext';

export interface TypingCommandsAPI {
  setTyping: (roomId: string, isTyping: boolean, timeoutMs?: number) => Promise<void>;
}

const TYPING_THROTTLE_MS = 3000;

export function useTypingCommands(): TypingCommandsAPI {
  const { client } = useMatrixContext();

  const lastSentRef = useRef<Map<string, number>>(new Map());

  const setTyping = useCallback(
    async (roomId: string, isTyping: boolean, timeoutMs: number = 3000): Promise<void> => {
      if (!client) {
        throw new Error('useTypingCommands must be used within MatrixProvider');
      }

      const room = client.getRoom(roomId);
      if (!room) {
        throw new Error(`Room ${roomId} not found`);
      }

      const now = Date.now();
      const lastSent = lastSentRef.current.get(roomId) ?? 0;

      if (!isTyping) {
        lastSentRef.current.set(roomId, now);
        await client.sendTyping(roomId, false, 0);
        return;
      }

      if (now - lastSent < TYPING_THROTTLE_MS) {
        return;
      }

      lastSentRef.current.set(roomId, now);
      await client.sendTyping(roomId, true, timeoutMs);
    },
    [client],
  );

  return { setTyping };
}
